import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Save, CheckCircle2, Paperclip } from "lucide-react";

const PERIODS = ["2024-Q1","2024-Q2","2024-Q3","2024-Q4","2025-Q1","2025-Q2","2025-Q3","2025-Q4","2026-Q1"];

export default function DataEntry() {
  const [search] = useSearchParams();
  const preselectKpi = search.get("kpi");
  const [catalogue, setCatalogue] = useState([]);
  const [kpiId, setKpiId] = useState("");
  const [period, setPeriod] = useState("2025-Q4");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [evidenceFile, setEvidenceFile] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/catalogue/kpis").then(r => {
      setCatalogue(r.data.kpis);
      setKpiId(prev => {
        if (prev || !r.data.kpis.length) return prev;
        const match = preselectKpi && r.data.kpis.find(k => k.id === preselectKpi);
        return match ? match.id : r.data.kpis[0].id;
      });
    });
  }, [preselectKpi]);

  useEffect(() => {
    if (kpiId) api.get("/measurements", { params: { kpi_id: kpiId } }).then(r => setRecent(r.data));
  }, [kpiId, msg]);

  const kpi = catalogue.find(k => k.id === kpiId);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.post("/measurements", {
        kpi_id: kpiId, period, value: Number(value),
        unit: kpi?.unit, source: "manual_entry", note,
      });
      let evMsg = "";
      if (evidenceFile) {
        const fd = new FormData();
        fd.append("file", evidenceFile);
        fd.append("kpi_id", kpiId);
        fd.append("period", period);
        await api.post("/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
        evMsg = ` Evidence "${evidenceFile.name}" attached.`;
      }
      setMsg(`Submitted ${value} ${kpi?.unit} for ${kpi?.name} (${period}).${evMsg}`);
      setValue(""); setNote(""); setEvidenceFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setErr(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="data-entry-root">
      <div>
        <div className="section-label">Data Entry</div>
        <h1 className="text-2xl font-bold tracking-tight mt-1">Manual KPI Measurement</h1>
        <p className="text-sm text-ink-soft">Submit a measurement for a KPI period. Approvers can post directly; contributors flag for review.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <form onSubmit={submit} className="card p-5 lg:col-span-1 space-y-4">
          <div>
            <label className="dlabel">KPI</label>
            <select
              data-testid="entry-kpi"
              value={kpiId} onChange={e=>setKpiId(e.target.value)}
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-brand"
            >
              {catalogue.map(k => <option key={k.id} value={k.id}>{k.name} · {k.material_issue}</option>)}
            </select>
          </div>
          <div>
            <label className="dlabel">Period</label>
            <select
              data-testid="entry-period"
              value={period} onChange={e=>setPeriod(e.target.value)}
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-brand"
            >
              {PERIODS.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="dlabel">Value ({kpi?.unit || "—"})</label>
            <input
              data-testid="entry-value"
              type="number" step="any" required
              value={value} onChange={e=>setValue(e.target.value)}
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-brand"
            />
          </div>
          <div>
            <label className="dlabel">Note (optional)</label>
            <textarea
              data-testid="entry-note"
              value={note} onChange={e=>setNote(e.target.value)}
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm h-20 outline-none focus:border-brand"
            />
          </div>
          <div>
            <label className="dlabel">Evidence (optional)</label>
            <div className="mt-1 flex items-center gap-2">
              <input
                data-testid="entry-evidence-file"
                ref={fileRef} type="file"
                accept=".pdf,.png,.jpg,.jpeg,.csv,.xlsx,.xls,.txt,.md,.docx"
                onChange={e=>setEvidenceFile(e.target.files[0])}
                className="text-xs"
              />
              {evidenceFile && (
                <span className="text-xs text-ink-soft flex items-center gap-1"><Paperclip size={12}/>{evidenceFile.name}</span>
              )}
            </div>
            <div className="text-[11px] text-ink-soft mt-1">Utility bills, certificates and audit reports raise data confidence.</div>
          </div>
          {msg && <div data-testid="entry-success" className="text-sm flex items-center gap-2 text-status-success border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2"><CheckCircle2 size={14}/> {msg}</div>}
          {err && <div data-testid="entry-error" className="text-sm text-status-error border border-red-200 bg-red-50 rounded-md px-3 py-2">{err}</div>}
          <button data-testid="entry-submit" disabled={busy} className="btn btn-primary w-full justify-center"><Save size={14}/> Submit measurement</button>
        </form>

        <div className="lg:col-span-2 card overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div>
              <div className="section-label">Recent measurements</div>
              <div className="text-xs text-ink-soft">{kpi?.name}</div>
            </div>
            <div className="flex gap-1 flex-wrap">{kpi?.standards.map(s=> <span key={s} className="pill text-[10px]">{s}</span>)}</div>
          </div>
          <table className="dense">
            <thead><tr><th>Period</th><th className="text-right">Value</th><th>Unit</th><th>Source</th><th>Confidence</th><th>Status</th><th>By</th></tr></thead>
            <tbody>
              {recent.map((m,i)=>(
                <tr key={i} data-testid={`recent-${i}`}>
                  <td className="font-mono text-xs">{m.period}</td>
                  <td className="num font-semibold">{m.value}</td>
                  <td>{m.unit}</td>
                  <td className="text-ink-soft text-xs">{m.source}</td>
                  <td className="font-mono text-xs">{Math.round(m.confidence*100)}%</td>
                  <td>
                    <span className={m.approved ? "pill pill-success" : "pill pill-warn"}>
                      {m.approved ? "approved" : "review"}
                    </span>
                  </td>
                  <td className="text-xs text-ink-soft">{m.created_by || "system"}</td>
                </tr>
              ))}
              {recent.length===0 && <tr><td colSpan={7} className="text-center text-ink-soft py-6">No measurements yet for this KPI.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
